/**
 * JsonObjectInput - textarea that parses its contents as a JSON object
 * on blur and commits back to the parent. Lifted verbatim from the
 * legacy `workflow-action-dialog.tsx` so every JSON editor in the
 * designer behaves identically (metadata, ui_hints, set, …).
 */

import { useState } from 'react'

interface JsonObjectInputProps {
  value: string
  onCommit: (next: Record<string, unknown>) => void
  /** Minimum rendered height in pixels; defaults to 96. */
  minHeight?: number
}

export function JsonObjectInput({
  value,
  onCommit,
  minHeight = 96,
}: JsonObjectInputProps) {
  const [draft, setDraft] = useState(value)
  const [error, setError] = useState<string | null>(null)

  return (
    <div className="flex flex-col gap-1">
      <textarea
        style={{ minHeight }}
        className="resize-y rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] px-3 py-2 font-mono text-[11px] text-[var(--ink)] focus:outline-none focus:ring-1 focus:ring-[var(--green-border)]"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          const trimmed = draft.trim()
          if (!trimmed) {
            onCommit({})
            setError(null)
            return
          }
          try {
            const parsed = JSON.parse(trimmed)
            if (
              parsed === null ||
              typeof parsed !== 'object' ||
              Array.isArray(parsed)
            ) {
              setError('Must be a JSON object.')
              return
            }
            onCommit(parsed as Record<string, unknown>)
            setError(null)
          } catch (err) {
            setError(err instanceof Error ? err.message : 'Invalid JSON.')
          }
        }}
        spellCheck={false}
      />
      {error && (
        <p className="text-[11px] text-[var(--status-error)]">{error}</p>
      )}
    </div>
  )
}

